import { isTechnicalContour } from '@/utils/technicalContour';
import { isWasteVerticalHost, WASTE_VERTICAL_ROUTE } from '@/utils/verticalHost';

const MOBILE_SHELL_BREAKPOINT_PX = 900;

export type MobileShellViewMode = 'auto' | 'desktop' | 'mobile';

export const getMobileShellBreakpoint = (): number => MOBILE_SHELL_BREAKPOINT_PX;

type LayoutGeometry = { width: number; height: number; angle: number | null };
const geometryByWindow = new WeakMap<object, LayoutGeometry>();

const positive = (...values: unknown[]): number => {
  for (const value of values) {
    const n = Number(value);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return Number.POSITIVE_INFINITY;
};

export const isCompactViewport = (): boolean => {
  if (typeof window === 'undefined') return false;
  // Layout geometry classifies the shell; visualViewport is reserved for usable
  // height. Pinch zoom and a visual-only keyboard must not change this mode.
  const doc = window.document?.documentElement;
  const width = positive(doc?.clientWidth, window.innerWidth);
  const height = positive(doc?.clientHeight, window.innerHeight);
  const rawAngle = window.screen?.orientation?.angle;
  const angle = typeof rawAngle === 'number' ? rawAngle : null;
  const previous = geometryByWindow.get(window);
  const active = window.document?.activeElement;
  const editing = active?.matches?.('textarea, input:not([type="button"]):not([type="submit"]), [contenteditable="true"]') ?? false;
  const sameOrientation = !previous || previous.angle === angle;
  // Some keyboards resize the layout viewport as well. Retain pre-keyboard
  // geometry while editing, but a width/real orientation change is a new layout.
  if (editing && previous && sameOrientation && Math.abs(width - previous.width) <= 2 && height < previous.height) {
    return previous.height > previous.width;
  }
  geometryByWindow.set(window, { width, height, angle });
  return height > width;
};

export const normalizeMobileShellViewMode = (value?: string | null): MobileShellViewMode => {
  const normalized = String(value ?? 'auto').trim().toLowerCase();
  if (normalized === 'desktop' || normalized === 'mobile' || normalized === 'auto') return normalized;
  return 'auto';
};

export const prefersMobileShell = (): boolean => isCompactViewport();

export const resolveEffectiveMobileShellViewMode = (value?: string | null): MobileShellViewMode => {
  void value;
  return prefersMobileShell() ? 'mobile' : 'desktop';
};

export const isMobileShellActive = (value?: string | null): boolean =>
  resolveEffectiveMobileShellViewMode(value) === 'mobile';

export const getAuthenticatedEntryRoute = (): string =>
  isWasteVerticalHost()
    ? WASTE_VERTICAL_ROUTE
    : isTechnicalContour()
      ? '/helpdesk?tab=contract'
      : prefersMobileShell()
        ? '/messages'
        : '/tickets';
